"use client";

import { useRef, useState } from "react";
import { cn } from "@/lib/utils";

const EXAMPLES = [
  {
    question: "What changed?",
    title: "Revenue grew. Margins told another story.",
    summary:
      "Revenue rose 7.6%, while operating margin fell 2.3 percentage points. The question is whether higher input costs are temporary or structural.",
    source: "Sample annual report · p. 42",
    excerpt:
      "Revenue increased from $17.1 million to $18.4 million. Operating margin declined from 23.8% to 21.5%, reflecting higher material costs.",
    metrics: [
      ["Revenue growth", "+7.6%", "Year over year"],
      ["Operating margin", "21.5%", "From 23.8%"],
    ],
  },
  {
    question: "Where is the headroom?",
    title: "The covenant holds. Headroom deserves a closer look.",
    summary:
      "Net leverage is 3.2× against a 4.0× limit. That leaves 0.8× of headroom, before considering changes in earnings or debt.",
    source: "Sample debt agreement · p. 18",
    excerpt:
      "The maximum permitted net leverage ratio is 4.0×. At the reporting date, the company’s net leverage ratio was 3.2×.",
    metrics: [
      ["Net leverage", "3.2×", "At the reporting date"],
      ["Headroom", "0.8×", "Against a 4.0× limit"],
    ],
  },
  {
    question: "What needs attention?",
    title: "A quiet paragraph. A material question.",
    summary:
      "Contracts representing 28% of revenue are due for renewal in the coming year. The next research question is how concentrated those renewals are.",
    source: "Sample risk disclosure · p. 27",
    excerpt:
      "Customer contracts representing 28% of annual revenue are scheduled for renewal during the next twelve months.",
    metrics: [
      ["Renewal exposure", "28%", "Of annual revenue"],
      ["Time horizon", "12 months", "Upcoming renewal period"],
    ],
  },
] as const;

export function ResearchExample() {
  const [active, setActive] = useState(0);
  const [selected, setSelected] = useState(false);
  const sourceRef = useRef<HTMLElement>(null);
  const example = EXAMPLES[active];
  return (
    <div>
      <div
        className="mb-5 flex flex-wrap gap-2"
        aria-label="Example research questions"
      >
        {EXAMPLES.map((item, index) => (
          <button
            key={item.question}
            type="button"
            aria-pressed={active === index}
            onClick={() => {
              setActive(index);
              setSelected(false);
            }}
            className={cn(
              "min-h-11 rounded-md border px-4 py-2 text-sm transition-colors",
              active === index
                ? "border-primary bg-primary text-primary-foreground"
                : "bg-background hover:bg-secondary",
            )}
          >
            <span className="mr-2 font-mono text-xs">0{index + 1}</span>
            {item.question}
          </button>
        ))}
      </div>
      <div className="overflow-hidden rounded-lg border bg-card shadow-elegant-lg">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-5 py-4 text-xs">
          <span>
            Northstar Industries{" "}
            <span className="text-muted-foreground">/ Research brief</span>
          </span>
          <span className="rounded-full border px-2 py-1 text-muted-foreground">
            Synthetic example
          </span>
        </div>
        <div className="grid md:grid-cols-2">
          <article className="p-6 sm:p-8" aria-live="polite" aria-atomic="true">
            <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              Analyst perspective
            </span>
            <h3 className="my-5 max-w-md text-3xl sm:text-4xl">
              {example.title}
            </h3>
            <p className="!text-base leading-relaxed">
              {example.summary}{" "}
              <button
                type="button"
                aria-label="Inspect source 1"
                aria-pressed={selected}
                onClick={() => {
                  setSelected(true);
                  sourceRef.current?.focus({ preventScroll: true });
                  sourceRef.current?.scrollIntoView({ block: "nearest" });
                }}
                className="inline-flex size-8 items-center justify-center rounded border border-gold/50 bg-gold/15 font-mono text-xs text-foreground"
              >
                1
              </button>
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 border-t pt-5">
              {example.metrics.map(([label, value, note]) => (
                <div key={label}>
                  <span className="font-mono text-[11px] uppercase text-muted-foreground">
                    {label}
                  </span>
                  <div className="my-1 font-serif text-4xl">{value}</div>
                  <p className="!text-xs">{note} · sample data</p>
                </div>
              ))}
            </div>
          </article>
          <aside
            ref={sourceRef}
            tabIndex={-1}
            aria-label="Illustrative source excerpt"
            className="border-t bg-secondary/30 p-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring md:border-l md:border-t-0 sm:p-8"
          >
            <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              Source 01 / illustrative excerpt
            </span>
            <h4 className="my-5 text-sm">Northstar · {example.source}</h4>
            <blockquote
              className={cn(
                "border-l-2 border-gold p-5 font-serif text-2xl leading-relaxed transition-colors",
                selected ? "bg-gold/15" : "bg-secondary/60",
              )}
            >
              {example.excerpt}
            </blockquote>
            <p className="mt-5 !text-xs" role="status">
              {selected
                ? "Source 1 selected. The excerpt supports the reported figures; the research question is interpretation."
                : "Select citation 1 to inspect the supporting passage."}
            </p>
          </aside>
        </div>
        <p className="border-t px-5 py-4 !text-xs">
          Illustrative workflow using a fictional company. No live data or AI
          requests are used in this example.
        </p>
      </div>
    </div>
  );
}
